import React, { useEffect, useMemo, useState } from 'react';
import { collection, query, where } from 'firebase/firestore';
import { db } from '../../../../firebase-client';
import { safeOnSnapshot } from '../../../../utils/firestoreSubscriptions';
import { isOutingPast } from '../../../../utils/outingTiming';

function toDateValue(value) {
  const date =
    value?.toDate?.() ?? (value instanceof Date ? value : value ? new Date(value) : null);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  return date;
}

function formatOutingDate(value, fromTime) {
  const date = toDateValue(value);
  if (!date) return 'Date TBC';
  return `${date.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  })}${fromTime ? ` • ${fromTime}` : ''}`;
}

function sortAssignments(left, right, direction = 'asc') {
  const leftDate = toDateValue(left?.outing?.date)?.getTime?.() ?? 0;
  const rightDate = toDateValue(right?.outing?.date)?.getTime?.() ?? 0;
  if (leftDate !== rightDate) {
    return direction === 'asc' ? leftDate - rightDate : rightDate - leftDate;
  }
  const leftUpdated = left?.outing?.updatedAt?.toDate?.()?.getTime?.() ?? 0;
  const rightUpdated = right?.outing?.updatedAt?.toDate?.()?.getTime?.() ?? 0;
  return rightUpdated - leftUpdated;
}

function crewStatusLabel(crew = {}, outingIsPast = false) {
  const status = String(crew?.status || 'pending').toLowerCase();
  if (status === 'allocated') {
    return crew?.selectedBoatName || crew?.selectedBoatId
      ? `Allocated to ${crew.selectedBoatName || crew.selectedBoatId}`
      : 'Allocated';
  }
  if (outingIsPast && status === 'unfilled') return 'Past outing';
  if (status === 'unfilled') return 'No boat allocated';
  return 'Pending allocation';
}

function crewStatusPill(status, outingIsPast = false) {
  const normalized = String(status || 'pending').toLowerCase();
  if (outingIsPast && normalized === 'unfilled') return 'info';
  if (normalized === 'allocated') return 'success';
  if (normalized === 'unfilled') return 'danger';
  return 'info';
}

export default function MyOutingsCard({
  teamId,
  userId,
  modules = {},
  onOpenEvent,
}) {
  const outingsEnabled = !!modules?.rowing?.outings?.enabled;
  const [dateFilter, setDateFilter] = useState('upcoming');
  const [outings, setOutings] = useState([]);
  const [assignments, setAssignments] = useState([]);

  useEffect(() => {
    if (!teamId || !userId || !outingsEnabled) {
      setOutings([]);
      return undefined;
    }

    const outingsQuery = query(
      collection(db, 'teams', teamId, 'outings'),
      where('status', '==', 'submitted')
    );

    const unsubscribe = safeOnSnapshot(
      outingsQuery,
      (snapshot) => {
        const rows = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
        setOutings(rows);
      },
      undefined,
      { label: 'rowing-member-outings', teamId }
    );

    return () => unsubscribe();
  }, [outingsEnabled, teamId, userId]);

  useEffect(() => {
    if (!teamId || !userId || !outingsEnabled || outings.length === 0) {
      setAssignments([]);
      return undefined;
    }

    const crewsByOuting = new Map();

    const publishAssignments = () => {
      const rows = outings
        .map((outing) => ({
          outing,
          crews: crewsByOuting.get(outing.id) || [],
        }))
        .filter((entry) => entry.crews.length > 0)
        .sort((left, right) => sortAssignments(left, right, 'asc'));
      setAssignments(rows);
    };

    const unsubscribes = outings.map((outing) =>
      safeOnSnapshot(
        query(
          collection(db, 'teams', teamId, 'outings', outing.id, 'crews'),
          where('memberIds', 'array-contains', userId)
        ),
        (snapshot) => {
          crewsByOuting.set(
            outing.id,
            snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          );
          publishAssignments();
        },
        undefined,
        { label: 'rowing-member-outing-crews', teamId, outingId: outing.id }
      )
    );

    publishAssignments();
    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
  }, [outings, outingsEnabled, teamId, userId]);

  const filteredAssignments = useMemo(() => {
    const filtered = assignments.filter(({ outing }) => {
      if (dateFilter === 'all') return true;
      const outingIsPast = isOutingPast(outing);
      if (dateFilter === 'past') return outingIsPast;
      return !outingIsPast;
    });

    return [...filtered].sort((left, right) =>
      sortAssignments(left, right, dateFilter === 'past' ? 'desc' : 'asc')
    );
  }, [assignments, dateFilter]);

  const summary = useMemo(
    () =>
      assignments.reduce(
        (acc, { outing, crews }) => {
          const outingIsPast = isOutingPast(outing);
          crews.forEach((crew) => {
            const status = String(crew?.status || 'pending').toLowerCase();
            if (status === 'allocated') acc.allocated += 1;
            else if (status === 'unfilled') {
              if (!outingIsPast) acc.unfilled += 1;
            }
            else acc.pending += 1;
          });
          return acc;
        },
        { allocated: 0, pending: 0, unfilled: 0 }
      ),
    [assignments]
  );

  if (!outingsEnabled || !teamId || !userId) return null;

  return (
    <div className="team-card">
      <div className="card-header-with-icon">
        <span className="card-title">My Outings</span>
      </div>

      <div className="card-body" style={{ padding: '0.9rem' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 120px), 1fr))',
            gap: 8,
          }}
        >
          <div className="card" style={{ borderRadius: 12, padding: 12, background: '#f8fafc' }}>
            <div className="text-muted" style={{ fontSize: '0.8rem', marginBottom: 4 }}>Allocated</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
              <strong>🚣</strong>
              <span className="availability-pill available">{summary.allocated}</span>
            </div>
          </div>
          <div className="card" style={{ borderRadius: 12, padding: 12, background: '#f8fafc' }}>
            <div className="text-muted" style={{ fontSize: '0.8rem', marginBottom: 4 }}>Pending</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
              <strong>⏳</strong>
              <span className="availability-pill pending">{summary.pending}</span>
            </div>
          </div>
          <div className="card" style={{ borderRadius: 12, padding: 12, background: '#f8fafc' }}>
            <div className="text-muted" style={{ fontSize: '0.8rem', marginBottom: 4 }}>No boat</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
              <strong>⚠️</strong>
              <span className="availability-pill unavailable">{summary.unfilled}</span>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
          <button
            type="button"
            className={dateFilter === 'upcoming' ? 'btn btn--primary btn--xs' : 'btn btn--secondary btn--xs'}
            onClick={() => setDateFilter('upcoming')}
          >
            Upcoming
          </button>
          <button
            type="button"
            className={dateFilter === 'all' ? 'btn btn--primary btn--xs' : 'btn btn--secondary btn--xs'}
            onClick={() => setDateFilter('all')}
          >
            All
          </button>
          <button
            type="button"
            className={dateFilter === 'past' ? 'btn btn--primary btn--xs' : 'btn btn--secondary btn--xs'}
            onClick={() => setDateFilter('past')}
          >
            Past
          </button>
        </div>

        {filteredAssignments.length === 0 ? (
          <p className="text-muted" style={{ marginTop: 12 }}>
            No outings assigned to you in this date range.
          </p>
        ) : (
          <div style={{ marginTop: 12, display: 'grid', gap: 8, maxHeight: '20rem', overflowY: 'auto', paddingRight: 4 }}>
            {filteredAssignments.map(({ outing, crews }) => {
              const outingIsPast = isOutingPast(outing);
              return (
              <div
                key={outing.id}
                className="card"
                style={{ borderRadius: 12, padding: 12, display: 'grid', gap: 10 }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'start' }}>
                  <div>
                    <strong>{outing.name || outing.linkedEventName || 'Outing'}</strong>
                    <div className="text-muted" style={{ marginTop: 4 }}>
                      {formatOutingDate(outing.date, outing.fromTime)}
                    </div>
                  </div>
                  {outing?.linkedEventId ? (
                    <button
                      type="button"
                      className="btn btn--secondary btn--xs"
                      onClick={() =>
                        onOpenEvent?.({
                          id: outing.linkedEventId,
                          originalId: outing.linkedEventId,
                        })
                      }
                    >
                      View event
                    </button>
                  ) : null}
                </div>

                <div style={{ display: 'grid', gap: 6 }}>
                  {crews.map((crew) => {
                    const members = Array.isArray(crew?.members) ? crew.members : [];
                    const memberSummary = members
                      .map((member) => {
                        const seatLabel =
                          typeof member?.seatLabel === 'string' && member.seatLabel.trim() !== ''
                            ? ` (${member.seatLabel.trim()})`
                            : '';
                        return `${member?.displayName || 'Member'}${seatLabel}`;
                      })
                      .join(', ');

                    return (
                      <div
                        key={crew.id}
                        style={{
                          border: '1px solid #e5e7eb',
                          borderRadius: 10,
                          padding: '0.65rem 0.75rem',
                          background: '#fff',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 12,
                            alignItems: 'start',
                          }}
                        >
                          <div>
                            <strong>{crew?.label || 'Crew'}</strong>
                            <div className="text-muted" style={{ marginTop: 4, fontSize: '0.9rem' }}>
                              {crewStatusLabel(crew, outingIsPast)}
                            </div>
                          </div>
                          <span className={`status-pill ${crewStatusPill(crew?.status, outingIsPast)}`}>
                            {String(crew?.status || 'pending').toLowerCase() === 'allocated'
                              ? 'Allocated'
                              : outingIsPast && String(crew?.status || 'pending').toLowerCase() === 'unfilled'
                              ? 'Past outing'
                              : String(crew?.status || 'pending').toLowerCase() === 'unfilled'
                              ? 'No boat'
                              : 'Pending'}
                          </span>
                        </div>
                        {memberSummary ? (
                          <div style={{ marginTop: 8, fontSize: '0.95rem' }}>{memberSummary}</div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
